import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import type { CuentaPorCobrarConCliente, PagoClienteConFormaPago, VentaDetalleConNombre } from '../../types/domain';

const COP = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

interface CarteraData {
  cuentas: CuentaPorCobrarConCliente[];
  abonosPorCuenta: Record<string, PagoClienteConFormaPago[]>;
}

/** Cartera de clientas (crédito a clientas): pedido explícito del usuario,
 * no forma parte del alcance original del DAF v2 -- ver
 * supabase/schema_cartera_clientes.sql para el diseño completo. Ventas
 * con saldo pendiente, abonos parciales vía el RPC `cobrar_cliente`
 * -- nunca se edita `saldo_pendiente` directamente, siempre queda el
 * rastro en `pagos_clientes`. Mismo patrón que CuentasPorPagar.tsx, del
 * lado de proveedores.
 *
 * Cada fila ya es una cuenta individual (una venta fiada = una cuenta,
 * ver `idx_cxc_venta_unica`), pero antes solo se mostraba el saldo de
 * esa cuenta como un número plano. Pedido del usuario: mostrar el
 * detalle de lo que debe, no solo el saldo -- así que además del join a
 * `clientes` ya existente, se agrega un join a `ventas` (fecha y total
 * de la venta que originó la cuenta) y una segunda consulta agrupada por
 * cuenta para el historial completo de abonos (`pagos_clientes`, con la
 * forma de pago resuelta) -- todo en dos round-trips, sin N+1 por fila.
 *
 * Segundo pedido del usuario (mismo hilo): que el detalle muestre además
 * QUÉ servicios/productos componen esa venta, no solo la fecha y el
 * total. Se anida `venta_detalle` dentro del join a `ventas` -- mismo
 * patrón de embed anidado que ya usa Reportes.tsx para el reporte de
 * ventas por servicio/producto -- resolviendo el nombre desde `servicios`
 * o `productos` según el `tipo` de cada línea. Sigue siendo un solo
 * round-trip adicional (va incluido en la primera consulta, no es una
 * tercera consulta), y no requiere RLS nueva: `venta_detalle_staff` ya
 * exige `is_staff()` vía la venta padre, y `servicios_read`/`productos_staff`
 * ya permiten lectura al mismo staff que puede ver Cartera. */
export function Cartera() {
  const [error, setError] = useState<string | null>(null);

  const {
    data,
    loading,
    refetch,
  } = useSupabaseQuery<CarteraData>(async () => {
    const { data: cuentasRaw, error: errorCuentas } = await supabase
      .from('cuentas_por_cobrar')
      .select(
        '*, clientes ( nombre, apellido ), ventas ( fecha, total, venta_detalle ( *, servicios ( nombre ), productos ( nombre ) ) )',
      )
      .neq('estado', 'pagada')
      .order('created_at', { ascending: false });

    if (errorCuentas) return { data: null, error: errorCuentas };

    const cuentas = (cuentasRaw as unknown as Array<Record<string, unknown>>).map((row) => {
      const cliente = row.clientes as { nombre: string; apellido: string | null } | null;
      const venta = row.ventas as {
        fecha: string;
        total: number;
        venta_detalle: Array<Record<string, unknown>> | null;
      } | null;
      const nombreCompleto = cliente ? [cliente.nombre, cliente.apellido].filter(Boolean).join(' ') : '—';

      const ventaItems = (venta?.venta_detalle ?? []).map((item) => {
        const servicio = item.servicios as { nombre: string } | null;
        const producto = item.productos as { nombre: string } | null;
        return {
          ...(item as unknown as VentaDetalleConNombre),
          nombre: (item.tipo === 'servicio' ? servicio?.nombre : producto?.nombre) ?? '—',
        };
      });

      return {
        ...(row as unknown as CuentaPorCobrarConCliente),
        cliente_nombre: nombreCompleto,
        venta_fecha: venta?.fecha ?? (row.created_at as string),
        venta_total: venta?.total ?? (row.monto_total as number),
        venta_items: ventaItems,
      };
    });

    const abonosPorCuenta: Record<string, PagoClienteConFormaPago[]> = {};

    if (cuentas.length > 0) {
      const { data: pagosRaw, error: errorPagos } = await supabase
        .from('pagos_clientes')
        .select('*, formas_pago ( nombre )')
        .in('cuenta_por_cobrar_id', cuentas.map((c) => c.id))
        .order('fecha', { ascending: false });

      if (errorPagos) return { data: null, error: errorPagos };

      for (const row of (pagosRaw ?? []) as unknown as Array<Record<string, unknown>>) {
        const formaPago = row.formas_pago as { nombre: string } | null;
        const abono = { ...(row as unknown as PagoClienteConFormaPago), forma_pago_nombre: formaPago?.nombre ?? '—' };
        const lista = abonosPorCuenta[abono.cuenta_por_cobrar_id] ?? [];
        lista.push(abono);
        abonosPorCuenta[abono.cuenta_por_cobrar_id] = lista;
      }
    }

    return { data: { cuentas, abonosPorCuenta }, error: null };
  });

  const cuentas = data?.cuentas ?? [];
  const totalCartera = cuentas.reduce((acc, cxc) => acc + cxc.saldo_pendiente, 0);

  function alAbonar() {
    setError(null);
    refetch();
  }

  return (
    <div>
      <PageHeader
        title="Cartera de clientas"
        description="Ventas fiadas a clientas con saldo pendiente de pago"
      />

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}
      {loading && <p className="text-sm text-onyx/50">Cargando…</p>}

      {data && cuentas.length > 0 && (
        <p className="mb-4 text-sm text-onyx/70">
          Total en cartera: <span className="font-medium text-onyx">{COP.format(totalCartera)}</span> en{' '}
          {cuentas.length} cuenta{cuentas.length === 1 ? '' : 's'} pendiente{cuentas.length === 1 ? '' : 's'}
        </p>
      )}

      {data && cuentas.length === 0 && <EmptyState title="No hay saldos pendientes de clientas" />}

      {data && cuentas.length > 0 && (
        <div className="space-y-3">
          {cuentas.map((cxc) => (
            <FilaCuenta
              key={cxc.id}
              cxc={cxc}
              abonos={data.abonosPorCuenta[cxc.id] ?? []}
              onAbonado={alAbonar}
              onError={setError}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FilaCuenta({
  cxc,
  abonos,
  onAbonado,
  onError,
}: {
  cxc: CuentaPorCobrarConCliente;
  abonos: PagoClienteConFormaPago[];
  onAbonado: () => void;
  onError: (msg: string | null) => void;
}) {
  const [mostrarAbono, setMostrarAbono] = useState(false);
  const [mostrarDetalle, setMostrarDetalle] = useState(false);
  const [monto, setMonto] = useState(String(cxc.saldo_pendiente));
  const [guardando, setGuardando] = useState(false);

  const abonado = cxc.monto_total - cxc.saldo_pendiente;

  async function abonar() {
    onError(null);
    setGuardando(true);
    const { error } = await supabase.rpc('cobrar_cliente', {
      p_cuenta_por_cobrar_id: cxc.id,
      p_monto: Number(monto) || 0,
      p_forma_pago_id: null,
    });
    setGuardando(false);

    if (error) {
      onError(error.message);
      return;
    }

    setMostrarAbono(false);
    onAbonado();
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-onyx">{cxc.cliente_nombre}</p>
          <p className="text-xs text-onyx/50">
            Total {COP.format(cxc.monto_total)} · Saldo pendiente{' '}
            <span className="font-medium text-onyx/80">{COP.format(cxc.saldo_pendiente)}</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`badge ${cxc.estado === 'parcial' ? 'bg-amber-100 text-amber-800' : 'bg-gray-200 text-gray-700'}`}>
            {cxc.estado}
          </span>
          <button type="button" className="btn-secondary" onClick={() => setMostrarDetalle((v) => !v)}>
            {mostrarDetalle ? 'Ocultar detalle' : 'Ver detalle'}
          </button>
          <button type="button" className="btn-secondary" onClick={() => setMostrarAbono((v) => !v)}>
            {mostrarAbono ? 'Cancelar' : 'Abonar'}
          </button>
        </div>
      </div>

      {mostrarDetalle && (
        <div className="mt-3 space-y-2 border-t border-onyx/10 pt-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-onyx/70 sm:grid-cols-4">
            <div>
              <p className="text-xs text-onyx/50">Fecha de la venta</p>
              <p className="text-onyx">
                {new Date(cxc.venta_fecha).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            </div>
            <div>
              <p className="text-xs text-onyx/50">Total de la venta</p>
              <p className="text-onyx">{COP.format(cxc.venta_total)}</p>
            </div>
            {cxc.venta_total !== cxc.monto_total && (
              <div>
                <p className="text-xs text-onyx/50">Monto fiado de esa venta</p>
                <p className="text-onyx">{COP.format(cxc.monto_total)}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-onyx/50">Abonado hasta ahora</p>
              <p className="text-onyx">{COP.format(abonado)}</p>
            </div>
          </div>

          <div>
            <p className="mb-1 text-xs text-onyx/50">Servicios y productos de esta venta</p>
            {cxc.venta_items.length === 0 ? (
              <p className="text-onyx/50">No se encontró el detalle de la venta.</p>
            ) : (
              <ul className="space-y-1">
                {cxc.venta_items.map((item) => (
                  <li key={item.id} className="flex justify-between text-onyx/70">
                    <span>
                      {item.nombre}
                      {item.cantidad > 1 ? ` × ${item.cantidad}` : ''}{' '}
                      <span className="text-xs text-onyx/40">
                        ({item.tipo === 'servicio' ? 'servicio' : 'producto'})
                      </span>
                    </span>
                    <span className="font-medium text-onyx">{COP.format(item.subtotal)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="mb-1 text-xs text-onyx/50">Historial de abonos</p>
            {abonos.length === 0 ? (
              <p className="text-onyx/50">Aún no se han registrado abonos a esta cuenta.</p>
            ) : (
              <ul className="space-y-1">
                {abonos.map((a) => (
                  <li key={a.id} className="flex justify-between text-onyx/70">
                    <span>
                      {new Date(a.fecha).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })} ·{' '}
                      {a.forma_pago_nombre}
                    </span>
                    <span className="font-medium text-onyx">{COP.format(a.monto)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {mostrarAbono && (
        <div className="mt-3 flex items-center gap-3 border-t border-onyx/10 pt-3">
          <input
            type="number"
            min={0}
            max={cxc.saldo_pendiente}
            step="0.01"
            className="input-field max-w-xs"
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
          />
          <button type="button" disabled={guardando} className="btn-primary" onClick={abonar}>
            {guardando ? 'Abonando…' : 'Confirmar abono'}
          </button>
        </div>
      )}
    </div>
  );
}
